import { useState } from "react";
import { Segment, Table, Button, Message, Popup } from "semantic-ui-react";
import { ICertificate } from "../../models/certificate";
import { useIntl } from "react-intl";
import { CertificateFormModal } from "./CertificateForm/CertificateFormModal";
import { CertificatesRow } from "./CertificatesRow";
import { UserRole } from "../../actions/authentificationActions";
import { useSelector } from "react-redux";
import { AppState } from "../../store/configureStore";
import { ICrewMember } from "../../models/crewMember";

type Props = {
    crewMember: ICrewMember;
    certificates: ICertificate[];
    fetchCrewMember: () => Promise<void>;
    dateOfBirth: string;
};

export const CertificatesTable = ({
    certificates,
    fetchCrewMember,
    dateOfBirth,
    crewMember,
}: Props) => {
    const user = useSelector((state: AppState) => state.user);
    const intl = useIntl();
    const [addModalOpen, setAddModalOpen] = useState(false);

    return (
        <section>
            <div className="container-header">
                <h2>{intl.formatMessage({ id: "certificatesOfQualification" })}</h2>
                {user.role !== UserRole.EnforcementAuthorityUser &&
                crewMember.guid.includes("ECDB_CREW_MEMBER") ? (
                    <Popup
                        content={intl.formatMessage({ id: "crewMemberNotInDb" })}
                        position="left center"
                        flowing
                        trigger={
                            <Button
                                id="disabled-button"
                                primary
                                content={intl.formatMessage({ id: "issueNewCertificate" })}
                                onClick={() =>
                                    crewMember.guid.includes("ECDB_CREW_MEMBER")
                                        ? null
                                        : setAddModalOpen(true)
                                }
                            />
                        }
                    />
                ) : user.role !== UserRole.EnforcementAuthorityUser ? (
                    <Button
                        primary
                        content={intl.formatMessage({ id: "issueNewCertificate" })}
                        onClick={() => setAddModalOpen(true)}
                    />
                ) : null}
            </div>
            <Segment>
                {certificates.length === 0 ? (
                    <Message
                        icon="info circle"
                        header={intl.formatMessage({ id: "noResultsFound" })}
                    />
                ) : (
                    <Table basic="very" compact="very">
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell singleLine width={1}>
                                    {intl.formatMessage({ id: "issuedBy" })}
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    {intl.formatMessage({ id: "type" })}
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    {intl.formatMessage({ id: "rkmStretch" })}
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    {intl.formatMessage({ id: "qualificationID" })}
                                </Table.HeaderCell>
                                <Table.HeaderCell width={1}>
                                    {intl.formatMessage({ id: "validUntil" })}
                                </Table.HeaderCell>
                                <Table.HeaderCell width={1}>
                                    {intl.formatMessage({ id: "status" })}
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    {intl.formatMessage({ id: "actions" })}
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {certificates.map(c => (
                                <CertificatesRow
                                    key={c.qualificationID}
                                    certificate={c}
                                    fetchCrewMember={fetchCrewMember}
                                    dateOfBirth={dateOfBirth}
                                    crewMember={crewMember}
                                    renewable={certificates.filter(x => x.type === c.type)[0] === c}
                                    authorizations={c.authorizations}
                                />
                            ))}
                        </Table.Body>
                    </Table>
                )}
            </Segment>
            <CertificateFormModal
                open={addModalOpen}
                close={() => setAddModalOpen(false)}
                fetchCrewMember={fetchCrewMember}
                dateOfBirth={dateOfBirth}
                certificates={certificates}
                crewMember={crewMember}
            />
        </section>
    );
};
